// src/pages/payment/paymentReceipt.js
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";

import { fetchPaymentRefundDetail } from "../../api/requests/payment-requests";
import { fetchCompanyLogo, fetchPublicCompanyLogo } from "../../api/requests/company-requests";
import {
    formatDateTime,
    getPaymentStatusText,
    getReceiptTypeText,
} from "../Tables/Paymenthistoryutils";
import { CARD_BRAND_BANK_LABELS } from "../../utils/paymentLinkUtils";
import logo from "../../assets/images/tahsilat360_logo2_trim.png";

/**
 * Ödeme dekontu (makbuz) üretimi — TEK kaynak.
 *
 * Aynı makbuz üç yerden açılıyor: ödeme geçmişi listesi, sağdan açılan detay
 * paneli ve tam detay sayfası. Daha önce yalnızca listede (PaymentHistoryTable)
 * tanımlıydı; sayfa da aynı çıktıyı üretebilsin diye buraya taşındı. Fonksiyonlar
 * bileşen state'ine dokunmaz, yükleniyor göstergesini çağıran yönetir.
 */
/**
 * İade dekontu için iade satırlarını çeker. Ödeme iade görmediyse hiç istek
 * atılmaz; istek başarısız olursa dekont iadesiz üretilir (makbuz hiç
 * açılmamasındansa iade bölümü olmadan açılsın).
 */
export const loadRefundDetailForReceipt = async (payment) => {
    const isRefunded = payment?.status === "REFUNDED" || payment?.status === "PARTIALLY_REFUNDED";
    if (!isRefunded) return null;
    try {
        const res = await fetchPaymentRefundDetail(payment.id);
        return res?.statusCode === 200 ? res.data : null;
    } catch {
        return null;
    }
};

/** Makbuz başlığındaki logonun piksel üst sınırı (2x baskı payıyla). */
const LOGO_MAX_W = 520;
const LOGO_MAX_H = 240;

/**
 * Blob'u makbuza gömülecek boyuta küçültüp veri URI'sine çevirir.
 *
 * Firmalar logolarını olduğu gibi yüklüyor; test firmasındaki dosya 1.7 MB. Bunu
 * ham hâliyle gömmek base64 ile ~2.3 MB'lık bir belge üretir ve html2canvas'ı
 * gereksiz yere yavaşlatır — oysa makbuzda 58px yüksekliğinde çiziliyor. Ölçek
 * KÜÇÜLTME yönünde: küçük logolar büyütülüp bulanıklaştırılmaz.
 * PNG seçildi çünkü JPEG saydam zeminleri siyaha boyar.
 */
const blobToScaledDataUrl = (blob) => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
        try {
            const oran = Math.min(1, LOGO_MAX_W / img.width, LOGO_MAX_H / img.height);
            const canvas = document.createElement("canvas");
            canvas.width = Math.max(1, Math.round(img.width * oran));
            canvas.height = Math.max(1, Math.round(img.height * oran));
            canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
            resolve(canvas.toDataURL("image/png"));
        } catch {
            resolve(null);
        } finally {
            URL.revokeObjectURL(url);
        }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
});

/**
 * Tahsilatı alan firmanın logosunu makbuz başlığı için VERİ URI'si olarak çeker.
 *
 * Veri URI şart: makbuz iki ayrı yoldan çiziliyor — yeni sekmeye document.write ile
 * yazılıyor ve html2canvas ile rasterize ediliyor. Harici bir kaynağa işaret eden
 * <img src> her iki yolda da boş çıkabilir (yeni sekmede blob ömrü biter, canvas'ta
 * kaynak "kirli" sayılır). Veri URI'si belgenin içinde taşındığı için ikisinde de
 * güvenle çizilir.
 *
 * İKİ UÇ denenir: firma-kimlikli uç `/api/admin/**` altında olduğu için yalnız
 * SUPER_ADMIN ve COMPANY_ADMIN erişebiliyor (SecurityConfig:189) — muhasebeci,
 * kasiyer ve cari kullanıcıları 403 alır. Onlar için alan-adına göre çalışan public
 * uca düşülür; kendi firmalarının paneli olduğu için sonuç aynı logodur. Süper admin
 * başka firmanın ödemesine bakarken doğru logoyu ilk uçtan alır.
 *
 * Hiçbiri sonuç vermezse null döner; başlık o zaman logosuz çizilir, düzen bozulmaz.
 */
export const loadCompanyLogoForReceipt = async (payment) => {
    const isImage = (res) => res?.ok && (res.headers.get("Content-Type") || "").startsWith("image/");
    try {
        let res = payment?.companyId ? await fetchCompanyLogo(payment.companyId) : null;
        if (!isImage(res)) {
            res = await fetchPublicCompanyLogo();
        }
        if (!isImage(res)) return null;
        return await blobToScaledDataUrl(await res.blob());
    } catch {
        return null;
    }
};

/**
 * Makbuzu indirmeden yeni sekmede gösterir. İndirme akışıyla AYNI içeriği
 * (createPDFContent) kullanır; fark, html2canvas/jsPDF'e hiç girmemesi —
 * kullanıcı makbuzu önce görüp sonra yazdırabilir ya da indirebilir.
 *
 * Pencere, iade isteği BEKLENMEDEN açılır: window.open kullanıcı hareketinin
 * hemen ardından çağrılmazsa tarayıcı açılır pencere engeline takılır.
 * İçerik veri geldikten sonra yazılır.
 */
export const viewPaymentReceipt = async (payment) => {
    if (!payment) return;
    const win = window.open("", "_blank");
    if (!win) {
        toast.error("Makbuz penceresi açılamadı. Tarayıcınızın açılır pencere engelini kontrol edin.");
        return;
    }
    const year = new Date(payment.paymentDate || Date.now()).getFullYear();
    const receiptNo = `TC-${year}-${String(payment.id || "").replace(/-/g, "").slice(-6).toUpperCase()}`;
    const [refundDetail, companyLogo] = await Promise.all([
        loadRefundDetailForReceipt(payment),
        loadCompanyLogoForReceipt(payment),
    ]);
    win.document.write(`<!doctype html>
<html lang="tr"><head><meta charset="utf-8" />
<title>Makbuz ${receiptNo}</title>
<style>
  body { margin:0; padding:24px; background:#eef1f5;
     font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; }
  .sheet { width:820px; max-width:100%; margin:0 auto; background:#fff; }
  @media print { body { background:#fff; padding:0; } .sheet { width:auto; } }
</style></head>
<body><div class="sheet">${createPDFContent(payment, refundDetail, companyLogo)}</div></body></html>`);
    win.document.close();
};

export const downloadPaymentReceiptPdf = async (payment) => {
    if (!payment) return;
    try {
        const [refundDetail, companyLogo] = await Promise.all([
            loadRefundDetailForReceipt(payment),
            loadCompanyLogoForReceipt(payment),
        ]);
        const pdfContent = createPDFContent(payment, refundDetail, companyLogo);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = pdfContent;
        tempDiv.style.position = 'absolute';
        tempDiv.style.left = '-9999px';
        tempDiv.style.top = '-9999px';
        tempDiv.style.width = '820px';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '0';
        tempDiv.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
        document.body.appendChild(tempDiv);

        const canvas = await html2canvas(tempDiv, {
            scale: 2,
            useCORS: true,
            backgroundColor: '#ffffff'
        });

        document.body.removeChild(tempDiv);

        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF();
        const pageWidth = 210;   // A4 genişliği (mm)
        const pageHeight = 297;  // A4 yüksekliği (mm)

        // Dekont her zaman TEK sayfa: içeriği A4 oranını koruyarak sayfaya sığdır.
        // İçerik sayfadan uzunsa yükseklik sınırına göre orantılı küçültülür (2. sayfaya taşmaz).
        let renderWidth = pageWidth;
        let renderHeight = (canvas.height * renderWidth) / canvas.width;
        if (renderHeight > pageHeight) {
            renderHeight = pageHeight;
            renderWidth = (canvas.width * renderHeight) / canvas.height;
        }
        const offsetX = (pageWidth - renderWidth) / 2;

        pdf.addImage(imgData, 'PNG', offsetX, 0, renderWidth, renderHeight);

        const paymentDate = payment.paymentDate
            ? new Date(payment.paymentDate).toLocaleDateString('tr-TR').replace(/\//g, '-')
            : 'tarih-yok';
        const fileName = `${payment.merchantOid || 'odeme'}_${paymentDate}.pdf`;

        pdf.save(fileName);
    } catch (error) {
        console.error('PDF oluşturma hatası:', error);
        toast.error('PDF oluşturulurken bir hata oluştu.');
    }
};

/**
 * @param payment      Liste satırı (PaymentHistoryDTO)
 * @param refundDetail İade edilmiş ödemelerde iade özeti + satırları; yoksa null.
 *                     Ödemenin kendi satırında iade TARİHİ taşınmadığı için ayrı
 *                     çekilir (bkz. loadRefundDetailForReceipt).
 * @param companyLogo  Tahsilatı alan firmanın logosu (veri URI). null ise başlıkta
 *                     logo yerine firma adı yazılır.
 */
export const createPDFContent = (payment, refundDetail = null, companyLogo = null) => {
    // ---- Renkler ----
    const NAVY = '#1c2f66';
    const NAVY_DARK = '#16264f';
    const ORANGE = '#f26a21';
    const GREEN = '#2E7D32';
    const TEXT = '#243b53';
    const MUTED = '#7b8794';
    const LINE = '#e4e8ef';
    const CARD_BG = '#f6f8fb';

    /**
     * HTML kaçırma. Makbuz metin birleştirmeyle kurulduğu için cari adı, adres,
     * açıklama gibi KULLANICI GİRDİSİ olan her alan buradan geçmek ZORUNDA —
     * aksi hâlde girdi veri değil kod olarak yorumlanır ve makbuzu açan kişinin
     * oturumunda çalışır (makbuz yeni sekmede de gösteriliyor, orada script de
     * çalışır). Aşağıdaki row/rowAddress/infoItem/infoChip yardımcıları kaçırmayı
     * kendi içinde yapar; yeni bir alan eklerken o yardımcıları kullan.
     */
    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
    ));

    /**
     * Makbuz ikonları — satır içi SVG.
     * Emoji kullanılmıyordu değil, kullanılıyordu; ancak emoji glifi işletim
     * sistemine göre değiştiği için (html2canvas ekrandakini rasterize eder)
     * aynı makbuz Mac'te renkli, Windows'ta farklı, bazı Linux'larda boş kare
     * çıkıyordu. SVG her makinede birebir aynı çizilir, harici font/dosya istemez.
     */
    const ICONS = {
        hash:     '<path d="M9.5 3 7.5 21M16.5 3l-2 18M3.5 8.5h17M2.5 15.5h17"/>',
        calendar: '<rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/>',
        card:     '<rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20M6 15h4"/>',
        layers:   '<path d="M12 3 3 8l9 5 9-5-9-5z"/><path d="M3 13l9 5 9-5"/>',
        status:   '<circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/>',
        errCode:  '<path d="M9 3h6l6 6v6l-6 6H9l-6-6V9l6-6z"/><path d="M12 8v5M12 16h.01"/>',
        warn:     '<path d="M12 3 2 20h20L12 3z"/><path d="M12 9v5M12 17h.01"/>',
        note:     '<path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z"/>',
        shield:   '<path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z"/><path d="M9 12l2 2 4-4"/>',
        check:    '<path d="M4 12.5 9.5 18 20 6.5"/>',
        close:    '<path d="M5 5l14 14M19 5 5 19"/>',
        hourglass:'<path d="M7 3h10M7 21h10M8 3v3.5L12 11l4-4.5V3M8 21v-3.5L12 13l4 4.5V21"/>',
        undo:     '<path d="M3 9h11a5 5 0 0 1 0 10H8"/><path d="M3 9l4-4M3 9l4 4"/>',
    };
    /** @param {string} name ICONS anahtarı @param {number} size px @param {string} color */
    const icon = (name, size = 16, color = 'currentColor') => `
      <svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}"
           stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"
           style="display:block; flex:0 0 auto;">${ICONS[name] || ''}</svg>`;

    // ---- Yardimci formatlayicilar (tasarima uygun: ₺ onekli, virgul ondalik) ----
    const nf2 = (x) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(x || 0);
    const nf4 = (x) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }).format(x || 0);
    const tl = (x) => '₺' + nf2(x);
    const fx = (x, sym) => sym + nf2(x);

    /**
     * Tutarın yazıyla karşılığı — matbu dekontların değiştirilmeye karşı klasik
     * güvencesi ("Yalnız ... TL"). Rakam sonradan oynanırsa yazı ile tutmaz.
     * Milyar mertebesine kadar yeterlidir; üstü bu üründe gerçekçi değil.
     */
    const BIRLER = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
    const ONLAR = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];
    const ucBasamak = (n) => {
        const yuz = Math.floor(n / 100), on = Math.floor((n % 100) / 10), bir = n % 10;
        // "biryüz" denmez, "yüz" denir.
        return (yuz ? (yuz === 1 ? '' : BIRLER[yuz]) + 'yüz' : '') + ONLAR[on] + BIRLER[bir];
    };
    const sayiyiYaziyaCevir = (value) => {
        const n = Math.floor(Math.abs(Number(value) || 0));
        if (n === 0) return 'sıfır';
        let kalan = n, out = '';
        // "birbin" denmez, "bin" denir; milyon/milyarda kural işlemez.
        for (const [carpan, ad] of [[1e9, 'milyar'], [1e6, 'milyon'], [1e3, 'bin']]) {
            const adet = Math.floor(kalan / carpan);
            if (adet) {
                out += (carpan === 1e3 && adet === 1 ? '' : ucBasamak(adet)) + ad;
                kalan %= carpan;
            }
        }
        return out + ucBasamak(kalan);
    };
    const tutarYaziyla = (value) => {
        const toplamKurus = Math.round((Number(value) || 0) * 100);
        const lira = Math.floor(toplamKurus / 100);
        const kurus = toplamKurus % 100;
        const metin = `${sayiyiYaziyaCevir(lira)} TL` + (kurus ? ` ${sayiyiYaziyaCevir(kurus)} kuruş` : '');
        return metin.charAt(0).toLocaleUpperCase('tr-TR') + metin.slice(1);
    };

    const usdAmount = payment.finalUsdRate ? payment.amount / payment.finalUsdRate : 0;
    const eurAmount = payment.finalEurRate ? payment.amount / payment.finalEurRate : 0;
    const gbpAmount = payment.finalGbpRate ? payment.amount / payment.finalGbpRate : 0;
    const hasCurrency = payment.finalUsdRate || payment.finalEurRate || payment.finalGbpRate;

    // ---- Durum rozeti ----
    const statusMap = {
        SUCCESS:            { label: 'ÖDENDİ',      sub: 'İşlem Başarıyla Tamamlandı', color: GREEN,     icon: 'check', doc: 'ödeme dekontu',        note: 'Fatura yerine geçmez.' },
        PENDING:            { label: 'BEKLEMEDE',   sub: 'Ödeme Bekleniyor',           color: '#d9a400', icon: 'hourglass',  doc: 'bekleyen ödeme dekontu', note: 'Bekleyen işlem dekontudur, fatura yerine geçmez.' },
        FAILED:             { label: 'BAŞARISIZ',   sub: 'İşlem Tamamlanamadı',        color: '#dc3545', icon: 'close', doc: 'başarısız ödeme dekontu', note: 'Başarısız işlem dekontudur, fatura yerine geçmez.' },
        REFUNDED:           { label: 'İADE EDİLDİ', sub: 'Tutar İade Edildi',          color: '#6c757d', icon: 'undo',  doc: 'iade dekontu',          note: 'İade dekontudur, fatura yerine geçmez.' },
        PARTIALLY_REFUNDED: { label: 'KISMİ İADE',  sub: 'Kısmen İade Edildi',         color: '#d9a400', icon: 'undo',  doc: 'kısmi iade dekontu',    note: 'Kısmi iade dekontudur, fatura yerine geçmez.' },
    };
    const st = statusMap[payment.status] || { label: getPaymentStatusText(payment.status), sub: '', color: NAVY, icon: 'status', doc: 'ödeme dekontu', note: 'Fatura yerine geçmez.' };
    const isSuccess = payment.status === 'SUCCESS';
    // Başarısız/bekleyen işlemde tutar "ödenen" değil, "işlem" tutarıdır
    const amountLabel = isSuccess ? 'ÖDENEN TUTAR' : 'İŞLEM TUTARI';

    // ---- Makbuz / islem meta ----
    const year = new Date(payment.paymentDate || Date.now()).getFullYear();
    const receiptNo = `TC-${year}-${String(payment.id || '').replace(/-/g, '').slice(-6).toUpperCase()}`;
    const rateDate = new Date().toLocaleDateString('tr-TR');
    const installmentText = payment.receiptType === 'CASH'
        ? 'Nakit'
        : (payment.installmentCount > 1 ? `${payment.installmentCount} Taksit` : 'Tek Çekim');

    /* Kart bilgisi: marka etiketi bankayı da içeriyor (BONUS → "Garanti BBVA Bonus"),
       bu yüzden ayrı bir "Banka" satırına gerek kalmıyor — banka adı ödemede zaten
       saklanmıyor. STANDART markası bir bankaya işaret etmez, etiketi "Banka Kartı /
       Dijital Banka"dır ve kart tipini tekrarlamamak için tek başına yazılır. */
    const cardTypeText = { CREDIT: 'Kredi Kartı', DEBIT: 'Banka Kartı' }[payment.cardType] || '';
    const cardBrandText = payment.cardBrand ? (CARD_BRAND_BANK_LABELS[payment.cardBrand] || payment.cardBrand) : '';
    const cardInfoText = [cardBrandText, payment.cardBrand === 'STANDART' ? '' : cardTypeText]
        .filter(Boolean)
        .join(' · ');

    // Taksitli işlemde taksit başına düşen tutar; tek çekimde anlamsız olduğu için yazılmaz.
    const installmentAmountText = (payment.installmentCount > 1 && payment.amount)
        ? tl(Number(payment.amount) / payment.installmentCount)
        : '';

    // ---- Kucuk yardimci: etiketli satir (deger yoksa gizle) ----
    const row = (label, value) => value
        ? `<div style="display:flex; margin:0 0 7px 0; font-size:13px; line-height:1.3;">
             <div style="width:112px; color:${MUTED}; font-weight:600; flex:0 0 112px;">${esc(label)}</div>
             <div style="color:${TEXT}; font-weight:600;">${esc(value)}</div>
           </div>`
        : '';

    /** Çok satırlı adres: önce kaçırılır, sonra satır sonları <br>'a çevrilir. */
    const rowAddress = (label, value) => value
        ? `<div style="display:flex; margin:0 0 7px 0; font-size:13px; line-height:1.3;">
             <div style="width:112px; color:${MUTED}; font-weight:600; flex:0 0 112px;">${esc(label)}</div>
             <div style="color:${TEXT}; font-weight:600;">${esc(value).replace(/\n/g, '<br>')}</div>
           </div>`
        : '';

    // ---- Bolum basligi (turuncu dikey aksan cizgisi) ----
    const sectionTitle = (title) => `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:11px;">
        <span style="display:inline-block; width:5px; height:18px; background:${ORANGE}; border-radius:3px;"></span>
        <span style="font-size:16px; font-weight:700; color:${NAVY};">${esc(title)}</span>
      </div>`;

    const subTitle = (title) => `
      <div style="font-size:12.5px; font-weight:700; color:${NAVY}; margin:2px 0 8px 0; padding-bottom:6px; border-bottom:1px solid ${LINE};">
        ${esc(title)}
      </div>`;

    // ---- Sol kart: Tahsilati Alan Firma ----
    // Not: Tahsilati alan firmanin iletisim/fatura detaylari (telefon, e-posta,
    // VKN, vergi dairesi, adres) su an odeme DTO'sunda gelmiyor; geldiginde asagidaki
    // satirlar otomatik dolar, gelmeyenler gizli kalir.
    const receivingCard = `
      <div style="flex:1; border:1px solid ${LINE}; border-radius:16px; padding:16px 22px;">
        ${sectionTitle('Tahsilatı Alan Firma')}
        ${subTitle('İletişim Bilgileri')}
        ${row('Firma Ünvanı', payment.companyName)}
        ${row('Vergi Dairesi', payment.companyTaxOffice || '-')}
        ${row('Telefon', payment.companyPhone)}
        ${row('E-posta', payment.companyEmail)}
        ${(payment.companyTaxNumber || payment.companyAddress) ? subTitle('Fatura Bilgileri') : ''}
        ${row('VKN', payment.companyTaxNumber)}
        ${rowAddress('Fatura Adresi', payment.companyAddress)}
      </div>`;

    // ---- Sag kart: Odeme Yapan Firma / Bayi ----
    const payingCard = `
      <div style="flex:1; border:1px solid ${LINE}; border-radius:16px; padding:16px 22px;">
        ${sectionTitle('Ödeme Yapan Firma / Bayi')}
        ${subTitle('İletişim Bilgileri')}
        ${row('Firma Ünvanı', payment.invoiceCompanyName || payment.accountName)}
        ${row('Vergi Dairesi', payment.invoiceTaxOffice || payment.accountTaxOffice || '-')}
        ${row('Cari Adı', payment.accountName)}
        ${row('Cari / Bayi Kodu', payment.accountCode)}
        ${row('Telefon', payment.accountPhone)}
        ${row('E-posta', payment.emailSnapshot)}
        ${(payment.invoiceTaxNumber || payment.accountTaxNumber || payment.invoiceTcNumber || payment.invoiceAddress) ? subTitle('Fatura Bilgileri') : ''}
        ${row('VKN', payment.invoiceTaxNumber || payment.accountTaxNumber)}
        ${row('TC Kimlik No', payment.invoiceTcNumber)}
        ${rowAddress('Fatura Adresi', payment.invoiceAddress)}
      </div>`;

    // ---- Islem bilgileri satiri (ikon + etiket + deger) ----
    // iconName: ICONS anahtari. value duz metindir ve kacirilir.
    const infoItem = (iconName, label, value) => `
      <div style="display:flex; align-items:flex-start; gap:12px; margin-bottom:11px;">
        <span style="width:20px; display:flex; justify-content:center; padding-top:1px; color:${NAVY};">${icon(iconName)}</span>
        <div style="flex:1;">
          <div style="font-size:12px; color:${MUTED}; font-weight:600; margin-bottom:2px;">${esc(label)}</div>
          <div style="font-size:13.5px; color:${TEXT}; font-weight:600;">${value ? esc(value) : '-'}</div>
        </div>
      </div>`;

    // ---- Durum/hata rozeti (referanstaki renkli chip) ----
    const statusChip = (text, color) => `
      <span style="display:inline-block; padding:5px 12px; border-radius:8px; border:1px solid ${color};
                   color:${color}; background:${color}14; font-weight:700; font-size:12.5px;">${esc(text)}</span>`;

    /**
     * Degeri duz metin yerine renkli rozet olan satir. Rozet HTML'i burada
     * uretilir; boylece infoItem'a hic ham HTML gecmez ve kacirma tek yerde kalir.
     */
    const infoChip = (iconName, label, text, color) => `
      <div style="display:flex; align-items:flex-start; gap:12px; margin-bottom:11px;">
        <span style="width:20px; display:flex; justify-content:center; padding-top:1px; color:${NAVY};">${icon(iconName)}</span>
        <div style="flex:1;">
          <div style="font-size:12px; color:${MUTED}; font-weight:600; margin-bottom:2px;">${esc(label)}</div>
          <div>${statusChip(text, color)}</div>
        </div>
      </div>`;

    // ---- Doviz satiri ----
    // symbol: para birimi simgesi ($/€/£). Once bayrak emojisi kullaniliyordu;
    // Windows bu glifleri hic cizmedigi icin makbuzda "US"/"EU"/"GB" harfleri
    // gorunuyordu. Simge rozeti her sistemde ayni cikar.
    const fxRow = (symbol, pair, rate, converted) => `
      <div style="display:flex; align-items:center; gap:14px; margin-bottom:8px;">
        <div style="flex:1; display:flex; align-items:center; gap:12px; border:1px solid ${LINE}; border-radius:12px; padding:10px 16px;">
          <span style="display:inline-flex; align-items:center; justify-content:center; width:26px; height:26px;
                       border-radius:8px; background:${CARD_BG}; color:${NAVY}; font-size:14px; font-weight:800;">${esc(symbol)}</span>
          <span style="font-weight:700; color:${NAVY}; font-size:14px;">${esc(pair)}</span>
          <span style="margin-left:auto; font-weight:700; color:${TEXT}; font-size:14px;">${rate}</span>
        </div>
        <span style="color:${ORANGE}; font-size:18px; font-weight:700;">&#8594;</span>
        <div style="flex:1; background:${CARD_BG}; border-radius:12px; padding:10px 16px; text-align:right; font-weight:700; color:${NAVY}; font-size:14px;">
          ${converted}
        </div>
      </div>`;

    const currencyHtml = hasCurrency ? `
      <div style="border:1px solid ${LINE}; border-radius:16px; padding:16px 22px; margin-bottom:14px;">
        ${sectionTitle('Döviz Karşılıkları')}
        <div style="font-size:12.5px; color:${MUTED}; font-weight:600; margin:-4px 0 12px 15px;">TCMB Satış Kuru (${rateDate})</div>
        ${payment.finalUsdRate ? fxRow('$', 'USD/TRY', nf4(payment.finalUsdRate), `${tl(payment.amount)} = ${fx(usdAmount, '$')}`) : ''}
        ${payment.finalEurRate ? fxRow('€', 'EUR/TRY', nf4(payment.finalEurRate), `${tl(payment.amount)} = ${fx(eurAmount, '€')}`) : ''}
        ${payment.finalGbpRate ? fxRow('£', 'GBP/TRY', nf4(payment.finalGbpRate), `${tl(payment.amount)} = ${fx(gbpAmount, '£')}`) : ''}
      </div>` : '';

    /* ---- Iade bilgileri ----
       Iade dekontunda daha once yalnizca ORIJINAL odeme tarihi ve tutari vardi;
       "ne zaman, ne kadar geri dondu" sorusu dekonttan cevaplanamiyordu.
       Tutar ozeti liste satirindan da (payment.totalRefunded) gelebildigi icin
       iade DETAYI cekilemese bile bolum tutarla cizilir, tarih '-' kalir. */
    const successfulRefunds = (refundDetail?.refunds || []).filter(r => r.status === 'SUCCESS');
    const totalRefunded = Number(refundDetail?.totalRefunded ?? payment.totalRefunded ?? 0);
    const isRefundedDoc = payment.status === 'REFUNDED' || payment.status === 'PARTIALLY_REFUNDED';
    // Dekonttaki "Iade Tarihi" = paranin gercekten geri dondugu son an.
    // completedAt yoksa (eski kayit) istek anina dusulur.
    const lastRefundAt = successfulRefunds
        .map(r => r.completedAt || r.requestedAt)
        .filter(Boolean)
        .sort()
        .pop() || null;
    const remainingAfterRefund = Math.max((payment.amount || 0) - totalRefunded, 0);

    // Birden fazla iade varsa tek tarih yetmez; her satir ayri gosterilir.
    const refundLine = (r) => `
      <div style="display:flex; align-items:center; gap:12px; padding:8px 0; border-top:1px solid ${LINE};">
        <span style="flex:1; font-size:12.5px; color:${TEXT}; font-weight:600;">
          ${esc(formatDateTime(r.completedAt || r.requestedAt))}
        </span>
        <span style="font-size:12.5px; color:${MUTED}; font-weight:600;">
          ${esc(r.refundClass === 'FULL' ? 'Tam İade' : 'Kısmi İade')}
        </span>
        <span style="width:120px; text-align:right; font-size:13px; color:${TEXT}; font-weight:700;">
          ${esc(tl(r.amount))}
        </span>
      </div>`;

    const refundHtml = (isRefundedDoc && totalRefunded > 0) ? `
      <div style="border:1px solid ${LINE}; border-radius:16px; padding:16px 22px; margin-bottom:14px;">
        ${sectionTitle('İade Bilgileri')}
        <div style="display:flex; gap:40px;">
          <div style="flex:1;">
            ${infoItem('calendar', 'İade Tarihi', lastRefundAt ? formatDateTime(lastRefundAt) : '-')}
            ${infoItem('undo', 'İade Edilen Tutar', tl(totalRefunded))}
          </div>
          <div style="flex:1;">
            ${infoItem('hash', 'Kalan Tutar', tl(remainingAfterRefund))}
            ${successfulRefunds.length > 1
                ? infoItem('layers', 'İade Sayısı', `${successfulRefunds.length} işlem`)
                : ''}
          </div>
        </div>
        ${successfulRefunds.length > 1 ? successfulRefunds.map(refundLine).join('') : ''}
      </div>` : '';

    const sourceText = payment.fromPaymentLink
        ? 'Ödeme bağlantısı üzerinden alınan'
        : 'Tahsilat360 üzerinden alınan';
    const descriptionText = `${sourceText} ${st.doc}`;

    const generationDate = formatDateTime(new Date());

    return `
  <div style="width:820px; margin:0 auto; background:#ffffff; font-family:'Segoe UI','Helvetica Neue',Arial,sans-serif; color:${TEXT};">

    <!-- Ust renk seridi -->
    <div style="display:flex; height:10px;">
      <div style="flex:2; background:${NAVY};"></div>
      <div style="flex:1; background:${ORANGE};"></div>
      <div style="flex:3; background:${NAVY};"></div>
    </div>

    <div style="padding:22px 44px 0 44px;">
      <!-- Baslik IKI SIRA halinde: ustte markalar (solda Tahsilat360, sagda
           tahsilati ALAN firmanin logosu), altta metinler (solda belge turu,
           sagda makbuz no). Iki sira ayri flex satirlari oldugu icin "ÖDEME
           DEKONTU" ile "Makbuz No" HER ZAMAN ayni hizada durur — logolarin
           yuksekligi degisse bile kaymazlar. Firma logosu cekilemezse (yetkisiz
           kullanici, logo yuklenmemis) ust sirada firma ADI kalir. -->
      <div style="margin-bottom:18px;">
        <div style="display:flex; justify-content:space-between; align-items:flex-end; gap:24px;">
          <img src="${logo}" alt="Tahsilat360" style="height:42px; width:auto; display:block;" />
          <div style="text-align:right; max-width:240px;">
            ${companyLogo
              ? `<img src="${companyLogo}" alt="${esc(payment.companyName || 'Firma')}"
                      style="max-height:58px; max-width:240px; width:auto; height:auto; object-fit:contain; display:block; margin-left:auto;" />`
              : `<div style="font-size:15px; font-weight:800; color:${NAVY}; line-height:1.3;">${esc(payment.companyName || '')}</div>`}
          </div>
        </div>
        <div style="display:flex; justify-content:space-between; align-items:baseline; gap:24px; margin-top:12px;">
          <div style="font-size:18px; font-weight:800; letter-spacing:1px; color:${NAVY};">ÖDEME DEKONTU</div>
          <div style="font-size:13px; color:${MUTED}; font-weight:600;">Makbuz No: ${receiptNo}</div>
        </div>
      </div>

      <!-- Tutar + Durum banneri -->
      <div style="display:flex; border-radius:18px; overflow:hidden; margin-bottom:20px;">
        <div style="flex:1.15; background:${NAVY}; padding:20px 32px; color:#fff;">
          <div style="font-size:13px; letter-spacing:2px; font-weight:600; opacity:.85;">${amountLabel}</div>
          <div style="font-size:38px; font-weight:800; margin-top:6px;">${tl(payment.amount)}</div>
          <div style="font-size:12px; font-weight:600; opacity:.8; margin-top:6px; line-height:1.35;">
            Yalnız ${esc(tutarYaziyla(payment.amount))}
          </div>
        </div>
        <div style="flex:1; background:${st.color}; padding:20px 32px; color:#fff; display:flex; align-items:center; justify-content:center; gap:16px;">
          <span style="display:inline-flex; align-items:center; justify-content:center; width:44px; height:44px; border-radius:50%; background:rgba(255,255,255,.22);">${icon(st.icon, 24, '#ffffff')}</span>
          <div>
            <div style="font-size:28px; font-weight:800; line-height:1.1;">${esc(st.label)}</div>
            ${st.sub ? `<div style="font-size:13px; font-weight:600; opacity:.92; margin-top:2px;">${st.sub}</div>` : ''}
          </div>
        </div>
      </div>

      <!-- Firma kartlari -->
      <div style="display:flex; gap:20px; margin-bottom:14px; align-items:stretch;">
        ${receivingCard}
        ${payingCard}
      </div>

      <!-- Islem bilgileri -->
      <div style="border:1px solid ${LINE}; border-radius:16px; padding:16px 22px; margin-bottom:14px;">
        ${sectionTitle('İşlem Bilgileri')}
        <div style="display:flex; gap:40px;">
          <div style="flex:1;">
            ${infoItem('hash', 'İşlem Kodu', payment.merchantOid)}
            ${infoItem('calendar', 'Ödeme Tarihi', formatDateTime(payment.paymentDate))}
            ${infoItem('card', 'Tahsilat Tipi / Ödeme Yöntemi', getReceiptTypeText(payment.receiptType))}
            ${payment.virtualPosLabel ? infoItem('shield', 'Sanal POS', payment.virtualPosLabel) : ''}
            ${infoItem('layers', 'Taksit', installmentText)}
            ${installmentAmountText ? infoItem('layers', 'Taksit Tutarı', installmentAmountText) : ''}
          </div>
          <div style="flex:1;">
            ${cardInfoText ? infoItem('card', 'Kart Bilgisi', cardInfoText) : ''}
            ${infoChip('status', 'Durum', st.label, st.color)}
            ${payment.failedReasonCode ? infoChip('errCode', 'Hata Kodu', payment.failedReasonCode, '#dc3545') : ''}
            ${payment.failedReasonMsg ? infoChip('warn', 'Hata Mesajı', payment.failedReasonMsg, '#dc3545') : ''}
            ${infoItem('note', 'Açıklama', descriptionText)}
          </div>
        </div>
      </div>

      <!-- Iade bilgileri: islem bilgilerinin hemen altinda, doviz blogunun ustunde -->
      ${refundHtml}

      <!-- Doviz karsiliklari -->
      ${currencyHtml}
    </div>

    <!-- Alt bilgi -->
    <div style="border-top:1px solid ${LINE}; margin-top:6px; padding:16px 44px 20px 44px; display:flex; justify-content:space-between; align-items:center;">
      <div style="display:flex; align-items:center; gap:14px;">
        <span style="display:inline-flex; align-items:center; justify-content:center; width:38px; height:38px; border-radius:10px; background:${CARD_BG}; color:${NAVY};">${icon('shield', 20)}</span>
        <div style="font-size:12.5px; color:${MUTED}; line-height:1.5;">
          Bu belge Tahsilat360 sistemi üzerinden otomatik oluşturulmuştur.<br>
          <span style="color:${TEXT}; font-weight:700;">${st.note}</span>
        </div>
      </div>
      <div style="text-align:right; font-size:12.5px; color:${MUTED}; line-height:1.5;">
        Oluşturma Tarihi<br>
        <span style="color:${TEXT}; font-weight:700;">${generationDate}</span>
      </div>
    </div>
  </div>
`;
};
